import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, File, Folder, FolderLock, Image, Loader2 } from "lucide-react";
import {
  fetchCoffreAttachFile,
  fetchCoffreAttachList,
  fetchCoffreUnlocked,
  fetchPhotosAttachFile,
  fetchPhotosAttachList,
  formatAttachSize,
  photosAttachThumbUrl,
  type ChatCoffreZone,
  type ChatSuiteEntry,
} from "@/lib/suite/chatAttachFromSuite";

type Kind = "coffre" | "photos";

type Props = {
  open: boolean;
  kind: Kind;
  onClose: () => void;
  onConfirm: (files: File[]) => void;
};

type PickItem = { path: string; name: string; size?: number; zone?: ChatCoffreZone };

const ZONES: { id: ChatCoffreZone; label: string }[] = [
  { id: "vault", label: "Coffre" },
  { id: "ged", label: "GED" },
];

export function ChatAttachPickerModal({ open, kind, onClose, onConfirm }: Props) {
  const [path, setPath] = useState("");
  const [zone, setZone] = useState<ChatCoffreZone>("vault");
  const [folders, setFolders] = useState<ChatSuiteEntry[]>([]);
  const [files, setFiles] = useState<ChatSuiteEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState<Map<string, PickItem>>(() => new Map());
  const openedAt = useRef(0);
  const confirmGen = useRef(0);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      confirmGen.current += 1;
      return;
    }
    openedAt.current = Date.now();
    confirmGen.current += 1;
    setBusy(false);
    setPath("");
    setZone("vault");
    setFolders([]);
    setFiles([]);
    setError(null);
    setLocked(false);
    setSelected(new Map());
    void reload("", "vault");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, kind]);

  async function reload(nextPath: string, nextZone = zone) {
    setLoading(true);
    setError(null);
    try {
      if (kind === "coffre") {
        const st = await fetchCoffreUnlocked();
        if (!st.ok || !st.unlocked) {
          setLocked(true);
          setFolders([]);
          setFiles([]);
          setError(st.message ?? "Déverrouillez le Coffre avec votre code d’accès.");
          return;
        }
        setLocked(false);
        const result = await fetchCoffreAttachList(nextPath, nextZone);
        if (result.error) {
          setError(result.error);
          setFolders([]);
          setFiles([]);
          return;
        }
        setPath(result.path);
        setFolders(result.entries.filter((e) => e.kind === "folder"));
        setFiles(result.entries.filter((e) => e.kind === "file"));
        return;
      }
      const result = await fetchPhotosAttachList(nextPath);
      if (result.error) {
        setError(result.error);
        setFolders([]);
        setFiles([]);
        return;
      }
      setPath(result.path);
      setFolders(result.folders);
      setFiles(result.files);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lecture impossible.");
    } finally {
      setLoading(false);
    }
  }

  const crumbs = path ? path.split("/").filter(Boolean) : [];
  const title = kind === "coffre" ? "Joindre depuis Coffre" : "Joindre depuis Photos";
  const Icon = kind === "coffre" ? FolderLock : Image;
  const selectedList = useMemo(() => [...selected.values()], [selected]);

  function toggleFile(entry: ChatSuiteEntry) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(entry.path)) next.delete(entry.path);
      else next.set(entry.path, { path: entry.path, name: entry.name, size: entry.size, zone });
      return next;
    });
  }

  if (!open) return null;

  return (
    <div
      className="chat-confirm-overlay fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => {
        if (Date.now() - openedAt.current < 480) return;
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      onPointerDown={(e) => {
        if (Date.now() - openedAt.current < 480) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        className="chat-confirm-dialog flex max-h-[min(82vh,36rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-attach-pick-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-[var(--chat-border)] px-4 py-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]">
            <Icon className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="chat-attach-pick-title" className="truncate text-sm font-semibold text-[var(--chat-text)]">
              {title}
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-[var(--chat-muted)]">
              {kind === "coffre" ? "Espace sécurisé" : "Bibliothèque Aurélia"}
            </p>
          </div>
          {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-[var(--chat-muted)]" /> : null}
        </div>

        {kind === "coffre" ? (
          <div className="flex gap-1 border-b border-[var(--chat-border)] px-3 py-2">
            {ZONES.map((z) => (
              <button
                key={z.id}
                type="button"
                className={`rounded-lg px-3 py-1 text-xs font-medium ${
                  zone === z.id
                    ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                    : "text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
                }`}
                onClick={() => {
                  setZone(z.id);
                  setSelected(new Map());
                  void reload("", z.id);
                }}
              >
                {z.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-1 border-b border-[var(--chat-border)] px-4 py-2 text-xs text-[var(--chat-muted)]">
          <button
            type="button"
            className="rounded px-1 py-0.5 hover:text-[var(--chat-accent-strong)]"
            onClick={() => void reload("", zone)}
          >
            {kind === "coffre" ? (zone === "ged" ? "GED" : "Coffre") : "Photos"}
          </button>
          {crumbs.map((part, index) => {
            const crumbPath = crumbs.slice(0, index + 1).join("/");
            return (
              <span key={crumbPath} className="inline-flex items-center gap-1">
                <ChevronRight className="size-3 opacity-60" aria-hidden />
                <button
                  type="button"
                  className="rounded px-1 py-0.5 hover:text-[var(--chat-accent-strong)]"
                  onClick={() => void reload(crumbPath, zone)}
                >
                  {part}
                </button>
              </span>
            );
          })}
        </div>

        {error ? (
          <p className="px-4 pt-2 text-xs text-[var(--chat-danger)]" role="alert">
            {error}
            {locked ? " Ouvrez le Coffre pour le déverrouiller, puis réessayez." : null}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loading && folders.length === 0 && files.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--chat-muted)]">
              <Loader2 className="size-4 animate-spin" />
              Chargement…
            </div>
          ) : folders.length === 0 && files.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--chat-muted)]">
              {locked ? "Coffre verrouillé." : "Aucun fichier ici."}
            </p>
          ) : (
            <ul className="space-y-0.5">
              {folders.map((folder) => (
                <li key={`d-${folder.path}`}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                    onClick={() => void reload(folder.path, zone)}
                  >
                    <Folder className="size-4 shrink-0 text-[var(--chat-muted)]" />
                    <span className="truncate">{folder.name}</span>
                  </button>
                </li>
              ))}
              {files.map((file) => {
                const on = selected.has(file.path);
                const thumb = kind === "photos" ? photosAttachThumbUrl(file.path) : null;
                return (
                  <li key={`f-${file.path}`}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-[var(--chat-surface-elevated)] ${
                        on ? "bg-[var(--chat-accent-soft)]" : "text-[var(--chat-text)]"
                      }`}
                      onClick={() => toggleFile(file)}
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="size-8 shrink-0 rounded-md object-cover"
                        />
                      ) : (
                        <File className="size-4 shrink-0 text-[var(--chat-muted)]" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{file.name}</span>
                        {file.size ? (
                          <span className="block text-[11px] text-[var(--chat-muted)]">
                            {formatAttachSize(file.size)}
                          </span>
                        ) : null}
                      </span>
                      {on ? <Check className="size-4 shrink-0 text-[var(--chat-accent-strong)]" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[var(--chat-border)] px-4 py-3">
          <p className="min-w-0 truncate text-[11px] text-[var(--chat-muted)]">
            {selectedList.length === 0
              ? "Sélectionnez un ou plusieurs fichiers"
              : selectedList.length === 1
                ? "1 fichier sélectionné"
                : `${selectedList.length} fichiers sélectionnés`}
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={busy}
              className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] disabled:opacity-50"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={busy || locked || selectedList.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--chat-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => {
                void (async () => {
                  const gen = confirmGen.current;
                  setBusy(true);
                  setError(null);
                  try {
                    const out: File[] = [];
                    for (const item of selectedList) {
                      const got =
                        kind === "coffre"
                          ? await fetchCoffreAttachFile(item.path, item.zone ?? zone, item.name)
                          : await fetchPhotosAttachFile(item.path, item.name);
                      if (gen !== confirmGen.current) return;
                      if (!got.file) {
                        throw new Error(got.error || "Fichier illisible.");
                      }
                      out.push(got.file);
                    }
                    if (gen !== confirmGen.current) return;
                    onConfirm(out);
                  } catch (err) {
                    if (gen !== confirmGen.current) return;
                    setError(err instanceof Error ? err.message : "Import impossible.");
                  } finally {
                    if (gen === confirmGen.current) setBusy(false);
                  }
                })();
              }}
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              Joindre
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
